import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react";
import * as React from "react";
import * as Yup from "yup";
import { THEME } from "../../theme/ThemeStore";
import { t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { ApplicationValues } from "../../types/models";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { CustomCheckbox } from "../ui/CustomCheckbox";
import {
    ErrorMessagesList,
    FileOverviewField,
    FormButtons,
    LinkOverviewField,
    SectionHeaderSmall
} from "../ui/Primitives";
import { getDataEntries } from "../util/helpers";
import { AAA_PRIVACY_LINK, DAL_PRIVACY_LINK } from "../../config";

type Props = {
    onPersonalData: () => void;
    onDetails: () => void;
    onDataUpload: () => void;
    onMotivation: () => void;
    onSubmit: (application: ApplicationValues) => void;
};

export const VerificationForm = observer(
    ({ onPersonalData, onDetails, onDataUpload, onMotivation, onSubmit }: Props) => {
        const isMobile = useMobileMediaQuery();

        const isDal = THEME.THEME_NAME === "dal";

        const { personalDataEntries, detailsEntries } = getDataEntries(
            applicationStore.currentApplication,
            "verification"
        );

        return (
            <>
                <Formik
                    initialValues={applicationStore.currentApplication}
                    onSubmit={onSubmit}
                    validationSchema={Yup.object().shape({
                        gdprAccepted: Yup.bool().oneOf([true], t("error.gdrp"))
                    })}
                    validateOnChange
                    validateOnBlur
                >
                    {({ isValid, dirty, values, errors, touched }) => {
                        React.useEffect(() => {
                            if (values.gdprAccepted) {
                                applicationStore.currentApplication.gdprAccepted = true;
                                applicationStore.isStepFinished["verification"] = true;
                            } else {
                                applicationStore.currentApplication.gdprAccepted = false;
                                applicationStore.isStepFinished["verification"] = false;
                            }
                        }, [dirty, isValid, values]);

                        return (
                            <Form
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexGrow: 1
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: !isMobile ? "row" : "column",
                                        justifyContent: "space-between",
                                        flexGrow: 1,
                                        marginBottom: isMobile ? 16 : 50,
                                        gap: isMobile ? 0 : 80
                                    }}
                                >
                                    <div
                                        style={{
                                            overflow: isDal || isMobile ? "visible" : "auto",
                                            flex: 1
                                        }}
                                    >
                                        {/* Persönliche Daten */}

                                        <VerificationSectionHeader
                                            label={t("screen.verify.personalData.title")}
                                            onClick={onPersonalData}
                                        />
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                margin: "10px 20px 40px 0"
                                            }}
                                        >
                                            {personalDataEntries.map((entry, index) => (
                                                <VerificationLine key={index} label={entry[0]} value={entry[1]} />
                                            ))}
                                        </div>
                                        {/* Details */}

                                        <VerificationSectionHeader
                                            label={t("screen.verify.details.title")}
                                            onClick={onDetails}
                                        />
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                margin: "10px 20px 40px 0"
                                            }}
                                        >
                                            {detailsEntries.map((entry, index) => (
                                                <VerificationLine key={index} label={entry[0]} value={entry[1]} />
                                            ))}
                                        </div>
                                        {/* Datenupload */}
                                        <div style={{ height: isMobile ? "unset" : 0, marginRight: 20 }}>
                                            <VerificationSectionHeader
                                                label={t("screen.verify.dataupload.title")}
                                                onClick={onDataUpload}
                                                style={{
                                                    marginBottom: isMobile ? 20 : "unset"
                                                }}
                                            />
                                            {applicationStore.currentApplication.resume && (
                                                <FileOverviewField
                                                    name={applicationStore.currentApplication.resume?.name}
                                                    data={applicationStore.currentApplication.resume}
                                                />
                                            )}
                                            {applicationStore.currentApplication.video && (
                                                <FileOverviewField
                                                    name={applicationStore.currentApplication.video?.name}
                                                    data={applicationStore.currentApplication.video}
                                                />
                                            )}
                                            {applicationStore.currentApplication.motivationalLetter && (
                                                <FileOverviewField
                                                    name={applicationStore.currentApplication.motivationalLetter?.name}
                                                    data={applicationStore.currentApplication.motivationalLetter}
                                                />
                                            )}
                                            {applicationStore.currentApplication.photo && (
                                                <FileOverviewField
                                                    name={applicationStore.currentApplication.photo?.name}
                                                    data={applicationStore.currentApplication.photo}
                                                />
                                            )}
                                            {applicationStore.currentApplication.portfolio && (
                                                <FileOverviewField
                                                    name={applicationStore.currentApplication.portfolio?.name}
                                                    data={applicationStore.currentApplication.portfolio}
                                                />
                                            )}
                                            {applicationStore.currentApplication.optionalFiles?.map((value, index) => (
                                                <FileOverviewField key={index} name={value.name} data={value} />
                                            ))}
                                            {applicationStore.currentApplication.optionalLinks?.map((value, index) => (
                                                <LinkOverviewField key={index} name={value} />
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        {/* Nachricht */}
                                        <div style={{ marginTop: isMobile ? 40 : 0, marginBottom: 40 }}>
                                            <VerificationSectionHeader
                                                label={t("screen.verify.motivation.title")}
                                                onClick={onMotivation}
                                            />
                                            <div
                                                style={{
                                                    marginTop: 20,
                                                    color: THEME.customColors.headingsDark,
                                                    wordBreak: "break-word"
                                                }}
                                                className="caption"
                                            >
                                                {applicationStore.currentApplication.motivation}
                                            </div>
                                        </div>
                                        {/* Datenschutz */}

                                        <SectionHeaderSmall label={t("screen.verify.terms.title")} />
                                        <div
                                            style={{
                                                marginTop: 20
                                            }}
                                            className="caption"
                                        >
                                            {t("screen.verify.terms.content")}
                                            <div
                                                onClick={() => {
                                                    window.open(
                                                        THEME.THEME_NAME === "aaa" ? AAA_PRIVACY_LINK : DAL_PRIVACY_LINK
                                                    );
                                                }}
                                                style={{
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                    color: THEME.customColors.headingsDark
                                                }}
                                            >
                                                {t("screen.verify.dataprotection.link")}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                margin: "10px 0 0"
                                            }}
                                        >
                                            <Field
                                                type="checkbox"
                                                name="gdprAccepted"
                                                component={CustomCheckbox}
                                                checked={values.gdprAccepted}
                                                style={{
                                                    marginTop: 10,
                                                    marginRight: 10
                                                }}
                                            />
                                            <p style={{ marginTop: 10, color: THEME.customColors.headingsDark }}>
                                                {t("screen.verify.terms.checkbox")}
                                            </p>
                                        </div>
                                        <ErrorMessagesList
                                            errors={errors}
                                            touched={touched}
                                            style={{ marginTop: 20 }}
                                        />
                                    </div>
                                </div>

                                <FormButtons
                                    labelBack={t("screen.details.button.back")}
                                    labelSubmit={t("screen.verify.button.submit")}
                                    middleButtons
                                    onBack={onMotivation}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </>
        );
    }
);

type VerificationLineProps = {
    label: string | undefined | null | number;
    value?: string | undefined | null | number;
};

export const VerificationLine = ({ label, value }: VerificationLineProps) => (
    <div style={{ marginTop: 8, display: "flex" }} className="caption">
        <div
            style={{
                flex: 1,
                marginRight: 30,
                wordBreak: "break-word"
            }}
        >
            {label}
        </div>
        <div
            style={{
                color: THEME.customColors.headingsDark,
                flex: 1,
                wordBreak: "break-word"
            }}
        >
            {value}
        </div>
    </div>
);

type VerificationSectionHeaderProps = { label: string; onClick: () => void; style?: React.CSSProperties };

export const VerificationSectionHeader = ({ label, onClick, style }: VerificationSectionHeaderProps) => (
    <div style={{ display: "flex", alignItems: "center", ...style }}>
        <SectionHeaderSmall label={label} />
        <img src={THEME.Images.icEdit} alt="" style={{ marginLeft: 10, cursor: "pointer" }} onClick={onClick} />
    </div>
);
